import { marked, type Token, type Tokens } from "marked";

export type TaskFieldsFromMarkdown = {
  /** Markdown rendered down to plain text suitable for the description field. */
  description: string;
  /** Absolute external URLs lifted out of the body for the task's links field. */
  links: string[];
};

/**
 * Split a notification's `bodyMarkdown` into the fields a Task form needs: a
 * plain-text `description` and a list of `links`. Absolute external URLs from
 * named markdown links (`[label](https://...)`) are surfaced as first-class task
 * links rather than left inline as noise, while the description keeps only the
 * readable label text.
 */
export function markdownToTaskFields(markdown: string | null | undefined): TaskFieldsFromMarkdown {
  return {
    description: markdownToPlainText(markdown),
    links: extractExternalLinks(markdown),
  };
}

/**
 * Convert markdown into readable plain text for contexts backed by a simple,
 * unformatted text field — e.g. a Task description created from a notification's
 * `bodyMarkdown`. Formatting markers that would otherwise leak through as literal
 * syntax (`**bold**`, `# headings`, backticks, `[label](url)` link/image syntax,
 * table pipes) are stripped while the human-readable content is preserved.
 *
 * Named links keep only their label text — the URL is dropped here and surfaced
 * separately via {@link extractExternalLinks}. Bare/autolinked URLs stay inline,
 * since removing them would leave a gap in the sentence. Lists keep simple `-` /
 * `1.` markers, which read fine as plain text.
 */
export function markdownToPlainText(markdown: string | null | undefined): string {
  if (!markdown || markdown.trim() === "") {
    return "";
  }
  const tokens = marked.lexer(markdown);
  return renderBlockTokens(tokens)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Collect the absolute external URLs from named markdown links, de-duplicated and
 * in document order, for use as the task's first-class links.
 *
 * Two kinds of links are deliberately excluded:
 * - Bare/autolinked URLs (label === href) are left inline in the description.
 * - Local/in-app links — relative routes such as `/o/<tenant>/facilities/...`,
 *   anchors, etc. — are dropped. They aren't valid standalone URLs and carry no
 *   meaning outside the running app, so by design only their label survives (in
 *   the description) and the href is discarded. See {@link isUsefulPlainTextUrl}.
 */
export function extractExternalLinks(markdown: string | null | undefined): string[] {
  if (!markdown || markdown.trim() === "") {
    return [];
  }
  const urls = new Set<string>();
  collectExternalLinks(marked.lexer(markdown), urls);
  return [...urls];
}

function collectExternalLinks(tokens: Token[], urls: Set<string>): void {
  for (const token of tokens) {
    if (isToken(token, "link")) {
      const label = renderInlineTokens(token.tokens);
      // Only named links to absolute external URLs become task links. Local/in-app
      // links (relative routes, anchors) are intentionally dropped here.
      if (label && label !== token.href && isUsefulPlainTextUrl(token.href)) {
        urls.add(token.href);
      }
    }
    if (isToken(token, "list")) {
      for (const item of token.items) {
        collectExternalLinks(item.tokens, urls);
      }
    } else if (isToken(token, "table")) {
      for (const cell of [...token.header, ...token.rows.flat()]) {
        collectExternalLinks(cell.tokens, urls);
      }
    } else if ("tokens" in token && token.tokens) {
      collectExternalLinks(token.tokens, urls);
    }
  }
}

/**
 * Narrowing helper. marked's `Token` union ends with a `Generic` fallback whose
 * `type` is `string`, which defeats `switch`/`===` control-flow narrowing. An
 * `Extract`-based predicate excludes `Generic` and resolves to the concrete token
 * type, so we avoid unsafe casts.
 */
function isToken<T extends Token["type"]>(
  token: Token,
  type: T
): token is Extract<Token, { type: T }> {
  return token.type === type;
}

function renderBlockTokens(tokens: Token[]): string {
  const blocks: string[] = [];
  for (const token of tokens) {
    const rendered = renderBlockToken(token);
    if (rendered.trim() !== "") {
      blocks.push(rendered);
    }
  }
  return blocks.join("\n\n");
}

function renderBlockToken(token: Token): string {
  if (isToken(token, "heading") || isToken(token, "paragraph")) {
    return renderInlineTokens(token.tokens);
  }
  if (isToken(token, "text")) {
    return token.tokens ? renderInlineTokens(token.tokens) : token.text;
  }
  if (isToken(token, "blockquote")) {
    return renderBlockTokens(token.tokens);
  }
  if (isToken(token, "list")) {
    return renderList(token);
  }
  if (isToken(token, "code")) {
    return token.text;
  }
  if (isToken(token, "table")) {
    return renderTable(token);
  }
  // space, hr, html, def — nothing meaningful for a plain-text field
  return "";
}

function renderList(list: Tokens.List): string {
  const start = typeof list.start === "number" ? list.start : 1;
  return list.items
    .map((item, index) => {
      const marker = list.ordered ? `${start + index}.` : "-";
      const content = renderBlockTokens(item.tokens).replace(/\n+/g, " ").trim();
      return `${marker} ${content}`;
    })
    .join("\n");
}

function renderTable(table: Tokens.Table): string {
  const rows = [table.header, ...table.rows];
  return rows
    .map((cells) => cells.map((cell) => renderInlineTokens(cell.tokens)).join(" | "))
    .join("\n");
}

function renderInlineTokens(tokens: Token[] | undefined): string {
  if (!tokens) {
    return "";
  }
  return tokens.map(renderInlineToken).join("");
}

function renderInlineToken(token: Token): string {
  if (isToken(token, "text")) {
    return token.tokens ? renderInlineTokens(token.tokens) : token.text;
  }
  if (isToken(token, "escape") || isToken(token, "codespan")) {
    return token.text;
  }
  if (isToken(token, "strong") || isToken(token, "em") || isToken(token, "del")) {
    return renderInlineTokens(token.tokens);
  }
  if (isToken(token, "br")) {
    return "\n";
  }
  if (isToken(token, "link")) {
    const label = renderInlineTokens(token.tokens);
    if (!label || label === token.href) {
      // Bare/autolinked URL — the href is the visible content, so keep it inline.
      return label || token.href || "";
    }
    // Named link — keep only the human-readable label. Any external URL is
    // surfaced separately as a task link (see extractExternalLinks); internal
    // app routes are dropped as unusable noise in a plain-text field.
    return label;
  }
  if (isToken(token, "image")) {
    return token.text;
  }
  // html and other raw markup — drop it rather than leak markup
  return "";
}

/**
 * Whether a link's URL should be carried out of the markdown as a real,
 * standalone URL. Only absolute `http(s)` URLs qualify.
 *
 * Local/in-app links — relative routes like `/o/<tenant>/facilities/...`, anchors
 * (`#section`), and other non-`http(s)` hrefs — intentionally return `false`.
 * This is the desired behaviour: such links are meaningless outside the running
 * app (they fail `URL` parsing / `yup.string().url()` validation and aren't
 * navigable as bare text), so they are dropped entirely rather than kept inline
 * or lifted into the task links field. Their human-readable label is preserved in
 * the description; only the local href is discarded.
 */
function isUsefulPlainTextUrl(href: string | null | undefined): boolean {
  if (!href) {
    return false;
  }
  return /^https?:\/\//i.test(href.trim());
}
